import {
  Center,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Encounter } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import { ResourceTable } from '@/components/resource-search/ResourceTable';
import {
  getEncounterSearchFields,
  getEncounterTableColumns,
  ENCOUNTER_SEARCH_DEFAULTS,
  type EncounterSearchParams,
} from './encounterSearchConfig';
import { useEncounterSearch } from './useEncounterSearch';
import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EncounterDetailsCard } from '@/components/EncounterDetailsCard';
import { useEncounterDetails } from './useEncounterDetails';

export const EncounterTab: FC = () => {
  const { patientId } = useParams();
  const medplum = useMedplum();
  const { t } = useTranslation(['encounters', 'patient-pages']);
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(
    null
  );

  // Use custom hook to fetch encounter details
  const {
    loading: modalLoading,
    patient: modalPatient,
    attendingDoctor,
    referringDoctor,
    consultants,
    additionalNotes,
    encounterStatus,
  } = useEncounterDetails(selectedEncounter, medplum);

  const {
    resources: encounters,
    loading,
    page,
    totalPages,
    setPage,
    patients,
    searchParams,
    updateSearchParams,
    clearSearch,
    fetchResources,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
  } = useEncounterSearch(medplum, patientId);

  const hidePatientName = Boolean(patientId);

  const handleSearch = useCallback(
    (updates: Partial<EncounterSearchParams>) => {
      const mergedParams = { ...searchParams, ...updates };
      setPage(1);
      updateSearchParams(updates);
      fetchResources(mergedParams);
    },
    [fetchResources, searchParams, setPage, updateSearchParams]
  );

  const handleClear = useCallback(() => {
    clearSearch(ENCOUNTER_SEARCH_DEFAULTS);
  }, [clearSearch]);

  const fields = useMemo(
    () =>
      getEncounterSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
        hidePatientName,
        patientNameQuery,
        setPatientNameQuery,
        setPatientQuery,
        patientNameOptions,
        patientLoading,
        patientNameLoading,
      }),
    [
      handleSearch,
      hidePatientName,
      patientLoading,
      patientNameLoading,
      patientNameOptions,
      patientNameQuery,
      searchParams,
      setPatientNameQuery,
      setPatientQuery,
      t,
    ]
  );

  const columns = useMemo(
    () =>
      getEncounterTableColumns({
        t,
        patients,
        hidePatientColumn: hidePatientName,
      }),
    [hidePatientName, patients, t]
  );

  const handleEncounterClick = useCallback((encounter: Encounter) => {
    setSelectedEncounter(encounter);
  }, []);

  const handleViewEncounter = useCallback((encounter: Encounter) => {
    setSelectedEncounter(encounter);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedEncounter(null);
  }, []);

  return (
    <Stack gap="md" className="p-6">
      {!patientId && (
        <Title order={2}>
          {t('title', { ns: 'encounters', defaultValue: 'Encounters' })}
        </Title>
      )}

      <ResourceSearchForm<EncounterSearchParams>
        fields={fields}
        searchParams={searchParams}
        onSearch={handleSearch}
        onClear={handleClear}
        clearLabel={t('filters.clearParams', {
          ns: 'encounters',
          defaultValue: 'Clear',
        })}
        layout={{ cols: { base: 1, sm: 2, lg: 3 } }}
      />

      <Paper shadow="sm" withBorder>
        {loading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : encounters.length === 0 ? (
          <Center p="xl">
            <Text c="dimmed">
              {t('table.empty', {
                ns: 'encounters',
                defaultValue: 'No encounters found',
              })}
            </Text>
          </Center>
        ) : (
          <ResourceTable<Encounter>
            data={encounters}
            columns={columns}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onRowClick={handleEncounterClick}
            onActionClick={handleViewEncounter}
            emptyText={t('filters.emptyTable', {
              ns: 'encounters',
              defaultValue: 'No encounters found',
            })}
          />
        )}
      </Paper>

      {/* Encounter Detail Modal */}
      <Modal
        opened={!!selectedEncounter}
        onClose={handleCloseModal}
        size="lg"
        title={t('viewModalTitle', {
          ns: 'encounters',
          defaultValue: 'View Encounter',
        })}
        centered
      >
        {selectedEncounter && (
          <EncounterDetailsCard
            encounter={selectedEncounter}
            patient={modalPatient}
            attendingDoctor={attendingDoctor}
            referringDoctor={referringDoctor}
            consultants={consultants}
            additionalNotes={additionalNotes}
            encounterStatus={encounterStatus}
            loading={modalLoading}
          />
        )}
      </Modal>
    </Stack>
  );
};
